import React from "react";
import { Button } from "@/components/ui/button";
import { MessageSquare, Bug, Lightbulb, RefreshCw, Volume2, VolumeX } from "lucide-react";

type ChatMode = 'chat' | 'debug' | 'recommend';

interface ModeSelectorProps {
  mode: ChatMode;
  onModeChange: (mode: ChatMode) => void;
  onClearChat: () => void;
  isTTSEnabled?: boolean;
  onToggleTTS?: () => void;
  isSpeechSupported?: boolean;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ 
  mode, 
  onModeChange, 
  onClearChat,
  isTTSEnabled = false,
  onToggleTTS,
  isSpeechSupported = false,
}) => {
  const modes = [
    { id: 'chat' as ChatMode, label: 'Chat', icon: MessageSquare, description: 'General Q&A' },
    { id: 'debug' as ChatMode, label: 'Debug', icon: Bug, description: 'Troubleshooting' },
    { id: 'recommend' as ChatMode, label: 'Recommend', icon: Lightbulb, description: 'Learning path' },
  ];

  return (
    <div className="flex items-center justify-between p-3 bg-muted/30">
      <div className="flex gap-2">
        {modes.map((m) => {
          const Icon = m.icon;
          return (
            <Button
              key={m.id}
              variant={mode === m.id ? "default" : "outline"}
              size="sm"
              onClick={() => onModeChange(m.id)}
              className="gap-2"
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{m.label}</span>
            </Button>
          );
        })}
      </div>
      
      <div className="flex gap-2">
        {isSpeechSupported && onToggleTTS && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleTTS}
            className={`gap-2 ${isTTSEnabled ? 'text-primary' : ''}`}
            title={isTTSEnabled ? 'Disable text-to-speech' : 'Enable text-to-speech'}
          >
            {isTTSEnabled ? (
              <>
                <Volume2 className="h-4 w-4" />
                <span className="hidden sm:inline">TTS On</span>
              </>
            ) : (
              <>
                <VolumeX className="h-4 w-4" />
                <span className="hidden sm:inline">TTS Off</span>
              </>
            )}
          </Button>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearChat}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="hidden sm:inline">Clear</span>
        </Button>
      </div>
    </div>
  );
};

export default ModeSelector;
